import React from "react";
import "./AppOverview.scss";

const sections = [
  { id: "profil",      icon: "👤", label: "Profil zaposlenog", color: "indigo"  },
  { id: "smene",       icon: "📅", label: "Smene",             color: "violet"  },
  { id: "finansije",   icon: "💰", label: "Finansije",         color: "emerald" },
  { id: "dashboard",   icon: "📊", label: "Dashboard",         color: "amber"   },
  { id: "prihod",      icon: "🧾", label: "Dnevni prihod",     color: "rose"    },
  { id: "rezervacije", icon: "🪑", label: "Rezervacije",       color: "sky"     },
  { id: "dokumenti",   icon: "📁", label: "Dokumenti",         color: "teal"    },
] as const;

type Color = typeof sections[number]["color"];

function Tag({ label, color }: { label: string; color: Color | "gray" | "slate" }) {
  return <span className={`ao-tag ao-tag--${color}`}>{label}</span>;
}

export default function AppOverview() {
  return (
    <div className="ao-wrap">
      <header className="ao-header">
        <div className="ao-header-brand">Fixman</div>
        <h1 className="ao-header-title">Pregled aplikacije</h1>
        <p className="ao-header-subtitle">
          Sveobuhvatan sistem za upravljanje poslovanjem, zaposlenima i finansijama.
          Ovaj dokument opisuje sve ključne module i njihove mogućnosti.
        </p>
      </header>

      <nav className="ao-toc">
        {sections.map((s) => (
          <a key={s.id} href={`#${s.id}`} className="ao-toc-btn">
            <span className="ao-toc-icon">{s.icon}</span>
            {s.label}
          </a>
        ))}
      </nav>

      <main className="ao-body">

        {/* ── 1. Profil zaposlenog ─────────────────────────────────────────── */}
        <section className="ao-section" id="profil">
          <div className="ao-section-header ao-section-header--indigo">
            <span className="ao-section-icon">👤</span>
            <div>
              <h2 className="ao-section-title">Profil zaposlenog</h2>
              <p className="ao-section-desc">Centralno mesto za sve informacije vezane za jednog radnika</p>
            </div>
          </div>
          <div className="ao-section-body">
            <div>
              <p className="ao-sub-title">Lični podaci i status</p>
              <ul className="ao-features">
                <li>Ime, prezime, email, telefon, pol, datum rođenja, lokacija</li>
                <li>Avatar sa mogućnošću upload-a fotografije</li>
                <li>Status naloga: <strong>aktivan</strong>, <strong>zamrznut</strong> ili <strong>nije aktiviran</strong></li>
                <li>Praćenje datuma rođendana — koliko dana do sledećeg, starost</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Ugovor i radni staž</p>
              <ul className="ao-features">
                <li>Datum početka i kraja ugovora sa vizuelnim progress barom</li>
                <li>Prikaz radnog staža u godinama i mesecima</li>
                <li>Upozorenje ako zdravstvena knjižica ističe za manje od 30 dana</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Tabs u profilu</p>
              <div className="ao-tags">
                {["Profil", "Isplate", "Odsustva", "Prekovremeni rad", "Ciljevi"].map(t => (
                  <Tag key={t} label={t} color="indigo" />
                ))}
              </div>
            </div>
            <div>
              <p className="ao-sub-title">Finansijska istorija</p>
              <ul className="ao-features">
                <li>Pregled svih isplata: plata, bonusi, kazne, pozajmice, prekovremeni rad, putni troškovi</li>
                <li>Aktivnost tekućeg meseca — sve finansijske promene u realnom vremenu</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Dokumenti zaposlenog</p>
              <ul className="ao-features">
                <li>Upload i preuzimanje ličnih dokumenata (PDF, slike, Word, Excel)</li>
                <li>Direktno zakazivanje odsustva ili prekovremenog rada iz profila</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Admin opcije</p>
              <ul className="ao-features">
                <li>Zamrzavanje naloga sa opcionalnim datumom isteka</li>
                <li>Odobravanje novih registracija (super admin)</li>
                <li>Dodela radnih lokacija i sektora zaposlenom</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── 2. Smene ─────────────────────────────────────────────────────── */}
        <section className="ao-section" id="smene">
          <div className="ao-section-header ao-section-header--violet">
            <span className="ao-section-icon">📅</span>
            <div>
              <h2 className="ao-section-title">Smene</h2>
              <p className="ao-section-desc">Upravljanje rasporedom rada svih zaposlenih po sedmici ili mesecu</p>
            </div>
          </div>
          <div className="ao-section-body">
            <div>
              <p className="ao-sub-title">Tipovi smena</p>
              <div className="ao-table-wrap">
                <table className="ao-table">
                  <thead>
                    <tr><th>Tip</th><th>Opis</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>HOURS</td><td>Radno vreme sa početkom i krajem (satnica)</td></tr>
                    <tr><td>DNEVNICA</td><td>Dnevna zarada bez fiksnih sati</td></tr>
                    <tr><td>ISPOMOC</td><td>Privremena pomoć</td></tr>
                    <tr><td>ODSUSTVO</td><td>Odsustvo (bolovanje, godišnji odmor i sl.)</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <p className="ao-sub-title">Kreiranje smena</p>
              <ul className="ao-features">
                <li><strong>Pojedinačna smena</strong> — jedan zaposleni, jedan datum, start/end vreme</li>
                <li><strong>Višestruke smene</strong> — obrazac za ponavljanje sa isključivanjem dana</li>
                <li><strong>Grupno odsustvo</strong> — bulk odsustvo sa definisanjem procenta plaćanja</li>
                <li><strong>Glasovni unos</strong> — kreiranje smene glasovnom komandom</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Prikaz i filtriranje</p>
              <ul className="ao-features">
                <li>Kalendarski grid po sedmici ili mesecu</li>
                <li>Pretraga zaposlenih po imenu, filter po lokaciji i sektoru</li>
                <li>Prikaz ukupnih sati po zaposlenom za odabrani period</li>
                <li>Kompaktni/prošireni prikaz, optimizovan mobilni layout</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── 3. Finansije ─────────────────────────────────────────────────── */}
        <section className="ao-section" id="finansije">
          <div className="ao-section-header ao-section-header--emerald">
            <span className="ao-section-icon">💰</span>
            <div>
              <h2 className="ao-section-title">Finansije</h2>
              <p className="ao-section-desc">Evidencija svih troškova, praćenje plaćanja i pregled prihoda</p>
            </div>
          </div>
          <div className="ao-section-body">
            <div>
              <p className="ao-sub-title">Izvori troškova</p>
              <div className="ao-grid">
                <div className="ao-card">
                  <p className="ao-card-title">Vlastiti unosi</p>
                  <p className="ao-card-text">Ručno uneseni troškovi prema dobavljačima sa fakturama i rokovima</p>
                </div>
                <div className="ao-card">
                  <p className="ao-card-title">Dnevni prihod</p>
                  <p className="ao-card-text">Troškovi uneseni kroz daily revenue modul automatski se ovde prikazuju</p>
                </div>
                <div className="ao-card">
                  <p className="ao-card-title">Fiksni troškovi</p>
                  <p className="ao-card-text">Automatski generisani periodični troškovi (zakup, pretplate i sl.)</p>
                </div>
                <div className="ao-card">
                  <p className="ao-card-title">Valute</p>
                  <p className="ao-card-text">RSD i EUR se prate odvojeno kroz celu aplikaciju</p>
                </div>
              </div>
            </div>
            <div>
              <p className="ao-sub-title">Statusi plaćanja</p>
              <div className="ao-tags">
                <Tag label="✓ Plaćeno" color="emerald" />
                <Tag label="✗ Neplaćeno" color="rose" />
                <Tag label="~ Delimično" color="amber" />
              </div>
            </div>
            <div>
              <p className="ao-sub-title">Filtriranje i pretraga</p>
              <ul className="ao-features">
                <li>Filter po lokaciji (multi-select), periodu (mesec / kvartal / godina)</li>
                <li>Filter po kategoriji troška — do 3 kategorije istovremeno</li>
                <li>Filter po dobavljaču (automatski se filtrira prema odabranoj kategoriji)</li>
                <li>Pregled neplaćenih troškova sa ažuriranjem statusa</li>
                <li>Export finansijskog izveštaja</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Sumarni prikazi</p>
              <ul className="ao-features">
                <li><strong>Payment Summary</strong> — plaćeno vs. neplaćeno/delimično</li>
                <li><strong>Revenue Summary</strong> — finansijski pregled prihoda (zahteva permisiju)</li>
                <li><strong>Plate zaposlenih</strong> — zbirni prikaz plata za odabrani period</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── 4. Dashboard ─────────────────────────────────────────────────── */}
        <section className="ao-section" id="dashboard">
          <div className="ao-section-header ao-section-header--amber">
            <span className="ao-section-icon">📊</span>
            <div>
              <h2 className="ao-section-title">Dashboard</h2>
              <p className="ao-section-desc">Brzi pregled stanja u organizaciji za odabrani mesec</p>
            </div>
          </div>
          <div className="ao-section-body">
            <div>
              <p className="ao-sub-title">Finansijski KPI pregled</p>
              <div className="ao-grid">
                <div className="ao-card"><p className="ao-card-title">Ukupan prihod</p><p className="ao-card-text">Ukupni prihod za odabrani period</p></div>
                <div className="ao-card"><p className="ao-card-title">Direktni troškovi</p><p className="ao-card-text">Nabavka hrane, pića, cigareta i sl.</p></div>
                <div className="ao-card"><p className="ao-card-title">Operativni troškovi</p><p className="ao-card-text">Zakup, marketing, aplikacije i sl.</p></div>
                <div className="ao-card"><p className="ao-card-title">Profit / Gubitak</p><p className="ao-card-text">Prihod minus svi troškovi i plate</p></div>
              </div>
            </div>
            <div>
              <p className="ao-sub-title">Pregled troškova po kategorijama</p>
              <ul className="ao-features">
                <li>Troškovi podeljeni na <strong>direktne</strong> i <strong>operativne</strong></li>
                <li>Svaka kategorija prikazuje procenat unutar grupe i procenat od ukupnog TOTAL-a</li>
                <li>Klik na kategoriju vodi direktno na filtrirani prikaz u Finansijama</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">People Insights</p>
              <div className="ao-tags">
                <Tag label="🎂 Rođendani" color="amber" />
                <Tag label="🏖 Odsustva" color="sky" />
                <Tag label="🆕 Novi zaposleni" color="emerald" />
                <Tag label="🏆 Jubileji" color="violet" />
                <Tag label="⚠️ Isticanje dokumenata" color="rose" />
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. Dnevni prihod ─────────────────────────────────────────────── */}
        <section className="ao-section" id="prihod">
          <div className="ao-section-header ao-section-header--rose">
            <span className="ao-section-icon">🧾</span>
            <div>
              <h2 className="ao-section-title">Dnevni prihod</h2>
              <p className="ao-section-desc">Svakodnevni unos prihoda i troškova po lokacijama</p>
            </div>
          </div>
          <div className="ao-section-body">
            <div>
              <p className="ao-sub-title">Kanali naplate</p>
              <div className="ao-table-wrap">
                <table className="ao-table">
                  <thead><tr><th>Kanal</th><th>Opis</th></tr></thead>
                  <tbody>
                    <tr><td>Gotovina</td><td>Fizički novac</td></tr>
                    <tr><td>Kartica</td><td>POS terminal</td></tr>
                    <tr><td>Prenos</td><td>Bankarski transfer</td></tr>
                    <tr><td>Interni keš</td><td>Interni cash registar</td></tr>
                    <tr><td>Dostava</td><td>Naplata putem platformi dostave</td></tr>
                    <tr><td>Ostalo</td><td>Svi ostali načini naplate</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <p className="ao-sub-title">Unos i odbitci</p>
              <ul className="ao-features">
                <li>Prihodi se unose po <strong>vrstama</strong> (konfigurabilno prema tipu poslovanja)</li>
                <li><strong>Expense matrix</strong> — odabir dobavljača/kategorije sa iznosom koji se oduzima</li>
                <li><strong>Keš registar</strong> — lista plaćanja iz kase (opis i iznos)</li>
                <li><strong>Extra prihodi</strong> — vanredni prihodi koji se dodaju</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Upravljanje vrstama prihoda</p>
              <ul className="ao-features">
                <li>Kreiranje, uređivanje i brisanje tipova prihoda</li>
                <li>Preraspoređivanje drag-and-drop-om po željenom redosledu</li>
                <li>Filter po lokaciji, navigacija po danima i mesecima</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── 6. Rezervacije ───────────────────────────────────────────────── */}
        <section className="ao-section" id="rezervacije">
          <div className="ao-section-header ao-section-header--sky">
            <span className="ao-section-icon">🪑</span>
            <div>
              <h2 className="ao-section-title">Rezervacije</h2>
              <p className="ao-section-desc">Upravljanje rezervacijama stolova za ugostiteljske objekte</p>
            </div>
          </div>
          <div className="ao-section-body">
            <div>
              <p className="ao-sub-title">Statusi rezervacija</p>
              <div className="ao-tags">
                <Tag label="✓ Potvrđena" color="emerald" />
                <Tag label="⏳ Na čekanju" color="amber" />
                <Tag label="✗ Otkazana" color="rose" />
                <Tag label="✔ Završena" color="slate" />
              </div>
            </div>
            <div>
              <p className="ao-sub-title">Prikaz i navigacija</p>
              <ul className="ao-features">
                <li>Tri načina prikaza: <strong>Dan</strong>, <strong>Sedmica</strong>, <strong>Mesec</strong></li>
                <li>Live upozorenja za nove i izmenjene rezervacije (osvežavanje svakih 60s)</li>
                <li>Pretraga po imenu, telefonu ili emailu gosta</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Upravljanje prostorom</p>
              <ul className="ao-features">
                <li>Vizuelni prikaz rasporeda stolova (<strong>floor plan</strong>)</li>
                <li>Dodela stolova rezervacijama, upravljanje kapacitetima</li>
                <li>Kreiranje posebnih događaja (eventi) sa posebnim rasporedom stolova</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Export i print</p>
              <ul className="ao-features">
                <li>Export rezervacija u <strong>PDF</strong> ili <strong>CSV</strong></li>
                <li>Print prikaz rezervacija</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── 7. Dokumenti ─────────────────────────────────────────────────── */}
        <section className="ao-section" id="dokumenti">
          <div className="ao-section-header ao-section-header--teal">
            <span className="ao-section-icon">📁</span>
            <div>
              <h2 className="ao-section-title">Dokumenti</h2>
              <p className="ao-section-desc">Hijerarhijski sistem za upravljanje dokumentacijom kompanije</p>
            </div>
          </div>
          <div className="ao-section-body">
            <div>
              <p className="ao-sub-title">Struktura</p>
              <ul className="ao-features">
                <li>Hijerarhija foldera sa neograničenim grananjem</li>
                <li>Levi panel: navigacija po folderima (stablo), desni panel: dokumenti</li>
                <li>Breadcrumb navigacija i pretraga kroz sve dokumente</li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Upravljanje pristupom</p>
              <ul className="ao-features">
                <li>Postavljanje <strong>javnog/privatnog</strong> pristupa po folderu</li>
                <li>Dodela permisija korisnicima ili grupama</li>
                <li>Definisanje uloga po folderu: <strong>pregled</strong>, <strong>upload</strong>, <strong>uređivanje</strong>, <strong>brisanje</strong></li>
              </ul>
            </div>
            <div>
              <p className="ao-sub-title">Podržani tipovi fajlova</p>
              <div className="ao-tags">
                <Tag label="PDF" color="rose" />
                <Tag label="Slike" color="sky" />
                <Tag label="Word" color="indigo" />
                <Tag label="Excel" color="emerald" />
                <Tag label="Ostalo" color="gray" />
              </div>
            </div>
          </div>
        </section>

        {/* ── Cross-cutting ─────────────────────────────────────────────────── */}
        <div className="ao-cross">
          <p className="ao-cross-title">Zajedničke funkcionalnosti</p>
          <div className="ao-cross-grid">
            {[
              { title: "Sistem permisija", text: "Granularna kontrola — svaki modul ima zasebne permisije po korisniku ili ulozi" },
              { title: "Višejezičnost", text: "Srpski i engleski jezik sa kompletnim prevodima" },
              { title: "Responsive dizajn", text: "Optimizovano za desktop i mobilne uređaje" },
              { title: "Cloud storage", text: "Svi fajlovi čuvaju se u Cloudflare R2 cloud storage-u" },
              { title: "Valute", text: "RSD i EUR se prate odvojeno kroz celu aplikaciju" },
              { title: "Keširanje", text: "Pametno keširanje API poziva ubrzava navigaciju" },
            ].map(item => (
              <div key={item.title} className="ao-cross-item">
                <p className="ao-cross-item-title">{item.title}</p>
                <p className="ao-cross-item-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

      </main>

      <footer className="ao-footer">
        Dokument pripremljen za potencijalne saradnike — interno.
      </footer>
    </div>
  );
}
